import React, { useState, useEffect, Fragment } from "react";
import useAuth from "../../hooks/useAuth";
import Button from "../../components/ui/Button";
import Spinner from "../../components/ui/Spinner";
import { User } from "../../model/User";
import Icon from "../../components/ui/Icon";
import { DEFAULT_ADMIN } from "../../model/User";
import { useTitle } from "../../components/TitleContext";
import TableData from "../../components/ui/Table";
import UserForm from "./userForm";
import { useDeleteUser } from "../../hooks/user/useDeleteUser";
import { useAddUser } from "../../hooks/user/useAddUser";
import { useUpdateUser } from "../../hooks/user/useUpdateUser";
import { useFetchUsers } from "../../hooks/user/useFetchUsers";
import { getUserColumns, getUserHiddenColumns } from "./userColumns";
import { useConfirmDialog } from "../../hooks/useConfirm";

const PageUsers = () => {
  const { setTitle } = useTitle();
  const { login: loggedUser } = useAuth();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const addUserMutation = useAddUser(() => setPopupOpen(false));
  const updateUserMutation = useUpdateUser(() => setPopupOpen(false));
  const deleteUserMutation = useDeleteUser();
  const { data: users = [], isLoading } = useFetchUsers();
  const confirm = useConfirmDialog();

  useEffect(() => {
    setTitle("Users");
  }, [setTitle]);

  const handleAddUser = () => {
    setEditingUser(null);
    setPopupOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setPopupOpen(true);
  };

  const handleSubmit = (data: {
    username: string;
    email: string;
    password?: string;
    confirmPassword?: string;
    roles: string[];
  }) => {
    const user = { ...editingUser, ...data };
    if (!data.password) {
      delete user.password;
    }
    if (!user.id) {
      addUserMutation.mutate(user as User);
    } else {
      updateUserMutation.mutate(user as User);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const isConfirmed = await confirm({
      title: "Confirm User Delete",
      message: "Are you sure you want to delete this user?",
    });
    if (isConfirmed) {
      deleteUserMutation.mutate(userId);
    }
  };

  // Check if user can be edited/deleted
  const isEditActionDisabled = (username: string) => username === DEFAULT_ADMIN.username;
  const isDeleteActionDisabled = (username: string) => loggedUser === username || username === DEFAULT_ADMIN.username;
  const columns = getUserColumns(isEditActionDisabled, isDeleteActionDisabled, handleEditUser, handleDeleteUser);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="container p-6 w-[90%]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-display font-bold">Users list</h2>
        <Button variant="secondary" onClick={handleAddUser}>
          <Icon icon="add">Add user</Icon>
        </Button>
      </div>
      <TableData
        tableName="usersTable"
        rows={users}
        columns={columns}
        pageSize={[5, 10, 15, 25]}
        hiddenColumns={getUserHiddenColumns()}
      />

      {isPopupOpen && (
        <UserForm
          isOpen={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onSubmit={handleSubmit}
          defaultValues={
            editingUser
              ? {
                  username: editingUser.username,
                  email: editingUser.email,
                  roles: editingUser.roles,
                }
              : {
                  username: "",
                  email: "",
                  password: "",
                  confirmPassword: "",
                  roles: [],
                }
          }
        />
      )}
    </div>
  );
};

export default PageUsers;
